import React, { useState, useEffect, useMemo } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { all_routes } from "../../../router/all_routes";
import { useCourseFeeShare } from "../hooks/useCourseFeeShare";
import { useFeeConfig } from "../../../feeconfig/courseFeeConfig/hooks/useFeeConfig";
import { useCourse } from "../../../configuration/course/hooks/useCourse";

const ViewCourseFeeShare = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { useGetFeeShareById } = useCourseFeeShare();
  const { useGetFeeConfigById } = useFeeConfig();
  const { getCourseState } = useCourse();

  // Get fee share data
  const { feeShare, loading: isLoadingFeeShare } = useGetFeeShareById(id || "");

  // Get course data for name references
  const { courses } = getCourseState();

  // State to store fee config data
  const [courseFeeConfigId, setCourseFeeConfigId] = useState<string>("");
  const { feeConfig: courseFeeConfig, loading: isLoadingCourseFeeConfig } =
    useGetFeeConfigById(courseFeeConfigId);

  // Set the fee config ID when fee share data is loaded
  useEffect(() => {
    if (feeShare && feeShare.course?.id) {
      setCourseFeeConfigId(feeShare.course.id.toString());
    }
  }, [feeShare]);

  // Combine stageFeeShare data with fee amount data from courseFeeConfig
  const stageFeesWithAmounts = useMemo(() => {
    if (!feeShare?.stageFeeShare || !courseFeeConfig?.coursefee?.stageFees) {
      return feeShare?.stageFeeShare || [];
    }

    return feeShare.stageFeeShare.map((stageShare) => {
      // Find corresponding fee amount from fee config
      const stageFee = courseFeeConfig.coursefee.stageFees.find(
        (sf) => sf.stage_id === stageShare.stage_id
      );

      // Find the stage name from the course data
      const course = courses.find((c) => c.id === feeShare.course?.id);
      const stageName =
        course?.courseStages?.find((cs) => cs.id === stageShare.stage_id)
          ?.name || `Stage ${stageShare.stage_id}`;

      return {
        ...stageShare,
        fee: stageFee?.amount,
        stage_name: stageName,
      };
    });
  }, [
    feeShare?.stageFeeShare,
    courseFeeConfig?.coursefee?.stageFees,
    courses,
    feeShare?.course?.id,
  ]);

  // Loading state
  const isLoading = isLoadingFeeShare || isLoadingCourseFeeConfig;

  if (isLoading) {
    return (
      <div className="page-wrapper">
        <div className="content container-fluid">
          <div className="card">
            <div className="card-body text-center py-5">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <p className="mt-2">Loading course fee share data...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!feeShare) {
    return (
      <div className="page-wrapper">
        <div className="content container-fluid">
          <div className="card">
            <div className="card-body text-center py-5">
              <div className="alert alert-warning">
                <h5>Fee share not found</h5>
                <p>The requested fee share record could not be found.</p>
                <Link
                  to={all_routes.courseFeeShareList}
                  className="btn btn-outline-primary mt-2"
                >
                  Return to Fee Share List
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page-wrapper">
      <div className="content">
        {/* Page Header */}
        <div className="d-md-flex d-block align-items-center justify-content-between mb-3">
          <div className="my-auto mb-2">
            <h3 className="mb-1">Course Fee Share Details</h3>
            <nav>
              <ol className="breadcrumb mb-0">
                <li className="breadcrumb-item">
                  <Link to={all_routes.adminDashboard}>Dashboard</Link>
                </li>
                <li className="breadcrumb-item">
                  <Link to={all_routes.courseFeeShareList}>
                    Course Fee Shares
                  </Link>
                </li>
                <li className="breadcrumb-item active" aria-current="page">
                  View Details
                </li>
              </ol>
            </nav>
          </div>
          <div className="d-flex gap-2">
            <button
              onClick={() => navigate(-1)}
              className="btn btn-outline-primary"
            >
              <i className="ti ti-arrow-left me-1"></i> Back
            </button>
            <Link
              to={all_routes.editCourseFeeShare.replace(":id", id || "")}
              className="btn btn-primary"
            >
              <i className="ti ti-edit me-1"></i> Edit
            </Link>
          </div>
        </div>

        {/* Basic Info Card */}
        <div className="card mb-3">
          <div className="card-header bg-light">
            <div className="d-flex align-items-center">
              <span className="bg-white avatar avatar-sm me-2 text-gray-7 flex-shrink-0">
                <i className="ti ti-info-square-rounded fs-16" />
              </span>
              <h4 className="text-dark mb-0">Basic Information</h4>
            </div>
          </div>
          <div className="card-body">
            <div className="row">
              <div className="col-md-6 mb-4">
                <div className="d-flex">
                  <div className="flex-shrink-0">
                    <div className="avatar avatar-sm bg-light-primary rounded">
                      <i className="ti ti-calendar fs-16 text-primary"></i>
                    </div>
                  </div>
                  <div className="flex-grow-1 ms-3">
                    <h6 className="fw-bold mb-1">Session</h6>
                    <p className="mb-0">
                      {feeShare.session?.name || "N/A"}
                    </p>
                  </div>
                </div>
              </div>
              <div className="col-md-6 mb-4">
                <div className="d-flex">
                  <div className="flex-shrink-0">
                    <div className="avatar avatar-sm bg-light-info rounded">
                      <i className="ti ti-book fs-16 text-info"></i>
                    </div>
                  </div>
                  <div className="flex-grow-1 ms-3">
                    <h6 className="fw-bold mb-1">Course</h6>
                    <p className="mb-0">
                      {feeShare.course?.name || "N/A"}
                    </p>
                  </div>
                </div>
              </div>
              <div className="col-md-6 mb-4">
                <div className="d-flex">
                  <div className="flex-shrink-0">
                    <div className="avatar avatar-sm bg-light-success rounded">
                      <i className="ti ti-coin fs-16 text-success"></i>
                    </div>
                  </div>
                  <div className="flex-grow-1 ms-3">
                    <h6 className="fw-bold mb-1">Fee Type</h6>
                    <p className="mb-0 text-capitalize">
                      {feeShare.feeType?.type === "coursefee"
                        ? "Course Fee"
                        : feeShare.feeType?.type || "Course Fee"}
                    </p>
                  </div>
                </div>
              </div>
              <div className="col-md-6 mb-4">
                <div className="d-flex">
                  <div className="flex-shrink-0">
                    <div className="avatar avatar-sm bg-light-warning rounded">
                      <i className="ti ti-percentage fs-16 text-warning"></i>
                    </div>
                  </div>
                  <div className="flex-grow-1 ms-3">
                    <h6 className="fw-bold mb-1">Share Type</h6>
                    <p className="fw-medium mb-0">
                      {feeShare.share_type === "fixed" ? (
                        <span className="badge badge-soft-primary d-inline-flex align-items-center">
                          <i className="ti ti-circle-filled fs-5 me-1"></i>
                          Fixed Amount (₹)
                        </span>
                      ) : (
                        <span className="badge badge-soft-success d-inline-flex align-items-center">
                          <i className="ti ti-circle-filled fs-5 me-1"></i>
                          Percentage (%)
                        </span>
                      )}
                    </p>
                  </div>
                </div>
              </div>
              <div className="col-md-6 mb-4">
                <div className="d-flex">
                  <div className="flex-shrink-0">
                    <div className="avatar avatar-sm bg-light-secondary rounded">
                      <i className="ti ti-clock fs-16 text-secondary"></i>
                    </div>
                  </div>
                  <div className="flex-grow-1 ms-3">
                    <h6 className="fw-bold mb-1">Created On</h6>
                    <p className="mb-0">
                      {new Date(feeShare.created_at).toLocaleDateString(
                        undefined,
                        {
                          year: "numeric",
                          month: "long",
                          day: "numeric",
                        }
                      )}
                    </p>
                  </div>
                </div>
              </div>
              <div className="col-md-6 mb-4">
                <div className="d-flex">
                  <div className="flex-shrink-0">
                    <div className="avatar avatar-sm bg-light-secondary rounded">
                      <i className="ti ti-refresh fs-16 text-secondary"></i>
                    </div>
                  </div>
                  <div className="flex-grow-1 ms-3">
                    <h6 className="fw-bold mb-1">Last Updated</h6>
                    <p className="mb-0">
                      {new Date(feeShare.updated_at).toLocaleDateString(
                        undefined,
                        {
                          year: "numeric",
                          month: "long",
                          day: "numeric",
                        }
                      )}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Stage Fees Card */}
        <div className="card mb-3">
          <div className="card-header bg-light">
            <div className="d-flex align-items-center">
              <span className="bg-white avatar avatar-sm me-2 text-gray-7 flex-shrink-0">
                <i className="ti ti-list-details fs-16" />
              </span>
              <h4 className="text-dark mb-0">Course Stage Fees</h4>
            </div>
          </div>
          <div className="card-body pb-1">
            {isLoadingCourseFeeConfig ? (
              <div className="text-center py-3">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
                <p className="mt-2">Loading fee configuration...</p>
              </div>
            ) : (
              <div className="table-responsive">
                <table className="table table-bordered">
                  <thead className="table-light">
                    <tr>
                      <th>Stage Name</th>
                      <th>Fee Amount</th>
                      <th>
                        Share Amount (
                        {feeShare.share_type === "fixed" ? "₹" : "%"})
                      </th>
                      {feeShare.share_type === "fixed" ? (
                        <th>Share Percentage</th>
                      ) : (
                        <th>Fixed Amount (₹)</th>
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {stageFeesWithAmounts.length > 0 ? (
                      stageFeesWithAmounts.map((stage) => (
                        <tr key={stage.id}>
                          <td>
                            {stage.stage_name || `Stage ${stage.stage_id}`}
                          </td>
                          <td>
                            {typeof stage.fee !== "undefined"
                              ? `₹${stage.fee.toLocaleString()}`
                              : "N/A"}
                          </td>
                          <td>
                            {feeShare.share_type === "fixed"
                              ? `₹${stage.share_amount.toLocaleString()}`
                              : `${stage.share_amount}%`}
                          </td>
                          {feeShare.share_type === "fixed" ? (
                            <td>
                              {stage.fee && stage.fee > 0
                                ? `${(
                                    (stage.share_amount / stage.fee) *
                                    100
                                  ).toFixed(2)}%`
                                : "N/A"}
                            </td>
                          ) : (
                            <td>
                              {stage.fee && stage.fee > 0
                                ? `₹${(
                                    (stage.share_amount / 100) *
                                    stage.fee
                                  ).toLocaleString()}`
                                : "N/A"}
                            </td>
                          )}
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={4} className="text-center">
                          No stage fee shares found
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>

        {/* Centers Card */}
        <div className="card mb-3">
          <div className="card-header bg-light">
            <div className="d-flex align-items-center">
              <span className="bg-white avatar avatar-sm me-2 text-gray-7 flex-shrink-0">
                <i className="ti ti-users fs-16" />
              </span>
              <h4 className="text-dark mb-0">Applied Centers</h4>
            </div>
          </div>
          <div className="card-body pb-1">
            {feeShare.all_centers ? (
              <div className="alert alert-info mb-0">
                <i className="ti ti-info-circle me-2"></i>
                This fee share is applied to all centers
              </div>
            ) : feeShare.centers && feeShare.centers.length > 0 ? (
              <div className="row">
                {feeShare.centers.map((center) => (
                  <div key={center.id} className="col-md-4 mb-3">
                    <div className="card border">
                      <div className="card-body p-3">
                        <h5 className="card-title mb-0">{center.name}</h5>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="alert alert-warning mb-0">
                <i className="ti ti-alert-circle me-2"></i>
                No centers found for this fee share
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ViewCourseFeeShare;
